import Component from '@glimmer/component';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import { t } from 'ember-intl';
import { PhCheck } from 'ember-phosphor-icons';
import Chip from 'tio-ui/components/chip';
import SectionPanel from 'tio-ui/components/section-panel';
import type IntlService from 'ember-intl/services/intl';
import type { ComponentLike } from '@glint/template';
import type { FieldDescriptor } from '../../../utils/tas/program-builder/field-groups';
import type { CustomFieldSignature } from 'tio-data/types/tuition-assistance';
import type { TasFieldOption as TasFieldOptionModel } from 'tio-data/data/resources/tas-field-option.type';
import type { TasProgramTemplateExtension as TASProgramTemplateModel } from 'tio-data/data/resources/tas-program-template.ext';

type FieldsSignature = TASProgramTemplateModel['fields'];
type IconComponent = ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
type ChipIntent = 'brand' | 'primary' | 'success' | 'warning' | 'default';

// Same friendly type labels + chip hues as the admin builder's FieldGroup so
// the read-only table stays visually in sync with what admins configure.
const TYPE_LABEL_KEYS: Record<FieldDescriptor['type'], string> = {
  text: 'program_builder.field_group.type_text',
  number: 'program_builder.field_group.type_number',
  date: 'program_builder.field_group.type_date',
  currency: 'program_builder.field_group.type_amount',
  textarea: 'program_builder.field_group.type_text_area',
  select: 'program_builder.field_group.type_dropdown',
  school: 'program_builder.field_group.type_institution',
};

function chipIntentFor(type: FieldDescriptor['type'], isDropdown: boolean): ChipIntent {
  if (isDropdown || type === 'select') {
    return 'brand';
  }
  if (type === 'currency') {
    return 'success';
  }
  if (type === 'number') {
    return 'primary';
  }
  if (type === 'date') {
    return 'warning';
  }
  return 'default';
}

/** Closest built-in field type for a custom field, so its Type chip matches. */
function customFieldType(field: CustomFieldSignature): FieldDescriptor['type'] {
  if (field.input === 'select' || field.input === 'radio') {
    return 'select';
  }
  if (field.input === 'textarea') {
    return 'textarea';
  }
  if (field.inputType === 'currency') {
    return 'currency';
  }
  if (field.inputType === 'date') {
    return 'date';
  }
  return 'text';
}

interface ReadoutFieldRow {
  name: string;
  label: string;
  note?: string;
  gate: boolean;
  visible: boolean;
  required: boolean;
  typeLabel: string;
  chipIntent: ChipIntent;
  /** Picklist choices shown under a dropdown's type chip. */
  options: string[];
  /** Company-managed (enum-locked) dropdown — options aren't template records. */
  companyManaged: boolean;
}

interface ReadoutFieldTableSignature {
  Args: {
    title: string;
    icon: IconComponent;
    /** Built-in field descriptors; live label/visible/required come from @fields. */
    descriptors?: FieldDescriptor[];
    fields: FieldsSignature;
    /** Names forced required + visible (e.g. credit-mode course fields). */
    forcedRequired?: string[];
    /** Admin-defined custom fields, rendered after the built-in rows. */
    customFields?: CustomFieldSignature[];
    /** The template's tas-field-option records — dropdown choices (built-in
     * picklists by field name; custom selects by custom-field id). */
    fieldOptions?: TasFieldOptionModel[];
    /** Reveal internal FIELD_NAMES badges (admin builder's devops toggle). */
    showFieldNames?: boolean;
  };
  Element: HTMLDivElement;
}

/** Sorted display labels for a field's tas-field-option records. */
function optionLabels(fieldName: string, records: TasFieldOptionModel[]): string[] {
  return records
    .filter((option) => option.fieldName === fieldName)
    .sort((a, b) => (a.ordinal ?? 0) - (b.ordinal ?? 0))
    .map((option) => String(option.label || option.value || '').trim())
    .filter(Boolean);
}

/**
 * Strict read-only counterpart of the admin builder's FieldGroup table: one row
 * per field with its configured label, a Type chip, and check/dash cells for
 * Visible and Required. No inputs and no option editors; internal field-name
 * badges are hidden unless `@showFieldNames` (the admin builder's devops
 * toggle) reveals them.
 */
export default class ReadoutFieldTable extends Component<ReadoutFieldTableSignature> {
  @service declare intl: IntlService;

  get rows(): ReadoutFieldRow[] {
    const config = this.args.fields as unknown as Record<
      string,
      | { label?: string; visible?: boolean; required?: boolean; value_as_field_option?: boolean }
      | undefined
    >;
    const forced = this.args.forcedRequired ?? [];
    const records = this.args.fieldOptions ?? [];

    const builtIns = (this.args.descriptors ?? []).map((descriptor) => {
      const current = config[descriptor.name] ?? {};
      const isForced = forced.includes(descriptor.name);
      const isGate = Boolean(descriptor.gate);
      // Same dropdown resolution as the builder: enum-locked and plain picklists
      // always are; an optional-picklist only once its config flag is set.
      const isDropdown = descriptor.enumLocked
        ? true
        : descriptor.optionalPicklist
          ? Boolean(current.value_as_field_option)
          : Boolean(descriptor.picklist);
      return {
        name: descriptor.name,
        label: current.label ?? descriptor.label,
        note: descriptor.note,
        gate: isGate,
        visible: isForced ? true : Boolean(current.visible),
        required: isGate ? false : isForced ? true : Boolean(current.required),
        typeLabel: isGate
          ? this.intl.t('program_builder.field_group.type_section_toggle')
          : this.intl.t(TYPE_LABEL_KEYS[descriptor.type]),
        chipIntent: chipIntentFor(descriptor.type, isDropdown),
        options: isDropdown && !descriptor.enumLocked ? optionLabels(descriptor.name, records) : [],
        companyManaged: Boolean(descriptor.enumLocked),
      };
    });

    const customs = (this.args.customFields ?? []).map((field) => {
      const type = customFieldType(field);
      // Custom SELECT options are tas-field-option records keyed by the custom
      // field's id; RADIO options live inline on the field (same split as the
      // builder's custom-field editor and the runtime form).
      const options =
        field.input === 'select'
          ? optionLabels(String(field.id ?? ''), records)
          : field.input === 'radio'
            ? (field.options ?? [])
                .map((option) => String(option.label ?? '').trim())
                .filter(Boolean)
            : [];
      return {
        name: field.name ?? field.label,
        label: field.label,
        note: undefined,
        gate: false,
        visible: Boolean(field.visible),
        required: Boolean(field.required),
        typeLabel: this.intl.t(TYPE_LABEL_KEYS[type]),
        chipIntent: chipIntentFor(type, type === 'select'),
        options,
        companyManaged: false,
      };
    });

    return [...builtIns, ...customs];
  }

  /**
   * Column template (Field · Type · Visible · Required). Inline style rather
   * than a Tailwind arbitrary class: this build's scanner doesn't emit
   * `grid-cols-[…]`, which collapses the table to a single column.
   */
  get gridStyle() {
    return htmlSafe('grid-template-columns: 1fr 9rem 4rem 4rem');
  }

  <template>
    <SectionPanel @icon={{@icon}} @title={{@title}} @iconColor="violet" ...attributes>
      <:body>
        <div class="space-y-2">
          <div
            class="tio-text-overline grid items-center gap-3 border-b tio-border-subtle pb-1 tio-fg-muted"
            style={{this.gridStyle}}
          >
            <span>{{t "program_builder.field_group.column_field"}}</span>
            <span>{{t "program_builder.field_group.column_type"}}</span>
            <span class="text-center">{{t "program_builder.field_group.column_visible"}}</span>
            <span class="text-center">{{t "program_builder.field_group.column_required"}}</span>
          </div>
          {{! Keyed by index: custom-field rows derive `name` from user-editable
          labels, which aren't guaranteed unique. }}
          {{#each this.rows key="@index" as |row|}}
            <div
              class="grid items-start gap-3"
              style={{this.gridStyle}}
              data-test-readout-field-row={{row.name}}
            >
              <div>
                <div class="tio-text-sm-medium">{{row.label}}</div>
                {{#if @showFieldNames}}
                  <code class="mt-1 block font-mono tio-text-micro tio-fg-brand">{{row.name}}</code>
                {{/if}}
                {{#if row.note}}
                  <p class="mt-1 text-[11px] leading-snug tio-fg-muted">{{row.note}}</p>
                {{/if}}
                {{! Options collapse behind a disclosure — values can run to
                hundreds of characters, so they get the wide column and wrap. }}
                {{#if row.options.length}}
                  <details class="mt-1">
                    <summary
                      class="cursor-pointer text-[11px] font-medium tio-fg-brand"
                      data-test-readout-options-summary={{row.name}}
                    >
                      {{t "program_builder.readout.options_count" count=row.options.length}}
                    </summary>
                    <ul class="m-0 mt-1 list-disc space-y-0.5 pl-4 text-xs tio-fg-muted">
                      {{#each row.options as |option|}}
                        <li class="break-words">{{option}}</li>
                      {{/each}}
                    </ul>
                  </details>
                {{/if}}
              </div>

              <div class="tio-col-extra-tight">
                <span class="tio-row-flush">
                  <Chip @size="sm" @intent={{row.chipIntent}} @appearance="default">
                    {{row.typeLabel}}
                  </Chip>
                </span>
                {{#if row.companyManaged}}
                  <span class="text-[11px] tio-fg-muted">{{t
                      "program_builder.field_group.company_managed"
                    }}</span>
                {{/if}}
              </div>

              {{! `relative` contains the absolutely-positioned sr-only spans —
              without it they escape the builder's scroll column and stretch
              the page. }}
              <div class="relative tio-row-center">
                {{#if row.visible}}
                  <PhCheck @size={{16}} class="tio-fg-success" aria-hidden="true" />
                  <span class="sr-only">{{t "program_builder.readout.field_visible"}}</span>
                {{else}}
                  <span class="tio-fg-faint" aria-hidden="true">—</span>
                  <span class="sr-only">{{t "program_builder.readout.field_hidden"}}</span>
                {{/if}}
              </div>
              <div class="relative tio-row-center">
                {{#unless row.gate}}
                  {{#if row.required}}
                    <PhCheck @size={{16}} class="tio-fg-success" aria-hidden="true" />
                    <span class="sr-only">{{t "program_builder.readout.field_required"}}</span>
                  {{else}}
                    <span class="tio-fg-faint" aria-hidden="true">—</span>
                    <span class="sr-only">{{t "program_builder.readout.field_optional"}}</span>
                  {{/if}}
                {{/unless}}
              </div>
            </div>
          {{/each}}
        </div>
      </:body>
    </SectionPanel>
  </template>
}
